import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import './FolderManager.css';

interface Folder {
  folderId: string;
  name: string;
  parentFolderId?: string;
  createdAt: string;
}

interface FolderManagerProps {
  currentFolderId?: string;
  onFolderChange?: (folderId?: string, folderName?: string) => void;
  onFolderCreated?: (folder: Folder) => void;
  onFolderDeleted?: (folderId: string) => void;
  externalOpen?: boolean;
  onExternalOpenHandled?: () => void;
  className?: string;
}

interface FolderTreeNode extends Folder {
  children: FolderTreeNode[];
  level: number;
}

const transformFolder = (raw: any): Folder => ({
  folderId: raw.folder_id,
  name: raw.name,
  parentFolderId: raw.parent_folder_id ?? undefined,
  createdAt: raw.created_at,
});

const FolderManager: React.FC<FolderManagerProps> = ({
  currentFolderId,
  onFolderChange,
  onFolderCreated,
  onFolderDeleted,
  externalOpen,
  onExternalOpenHandled,
  className = ''
}) => {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creating, setCreating] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);

  const { folders: foldersApi } = useApi();
  const { user } = useAuth();

  useEffect(() => {
    if (user) loadFolders();
  }, [user]);

  useEffect(() => {
    buildFolderTree();
  }, [folders]);

  useEffect(() => {
    if (showCreateModal) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setNewFolderName('');
      setError('');
    }
  }, [showCreateModal]);

  useEffect(() => {
    if (externalOpen) {
      setShowCreateModal(true);
      onExternalOpenHandled?.();
    }
  }, [externalOpen]);

  const loadFolders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await foldersApi.getFolders();
      setFolders((response.folders || []).map(transformFolder));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const buildFolderTree = () => {
    const folderMap = new Map<string, FolderTreeNode>();
    folders.forEach(folder => {
      folderMap.set(folder.folderId, { ...folder, children: [], level: 0 });
    });

    const rootNodes: FolderTreeNode[] = [];
    folders.forEach(folder => {
      const node = folderMap.get(folder.folderId)!;
      if (folder.parentFolderId) {
        const parent = folderMap.get(folder.parentFolderId);
        if (parent) {
          parent.children.push(node);
          node.level = parent.level + 1;
        } else {
          rootNodes.push(node);
        }
      } else {
        rootNodes.push(node);
      }
    });

    const sortNodes = (nodes: FolderTreeNode[]) => {
      nodes.sort((a, b) => a.name.localeCompare(b.name));
      nodes.forEach(node => sortNodes(node.children));
    };
    sortNodes(rootNodes);
    setFolderTree(rootNodes);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      setCreating(true);
      setError('');
      const raw = await foldersApi.createFolder(newFolderName.trim(), currentFolderId);
      const folder = transformFolder(raw);
      setFolders(prev => [...prev, folder]);
      setShowCreateModal(false);
      if (onFolderCreated) onFolderCreated(folder);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteFolder = async (folder: Folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? This cannot be undone.`)) return;

    try {
      setError('');
      await foldersApi.deleteFolder(folder.folderId);
      setFolders(prev => prev.filter(f => f.folderId !== folder.folderId));
      if (onFolderDeleted) onFolderDeleted(folder.folderId);
      if (currentFolderId === folder.folderId && onFolderChange) {
        onFolderChange(folder.parentFolderId, undefined);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete folder');
    }
  };

  const toggleExpansion = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      next.has(folderId) ? next.delete(folderId) : next.add(folderId);
      return next;
    });
  };

  const renderFolderNode = (node: FolderTreeNode): React.ReactNode => {
    const isExpanded = expandedFolders.has(node.folderId);
    const hasChildren = node.children.length > 0;
    const isSelected = currentFolderId === node.folderId;

    return (
      <div key={node.folderId} className="folder-node">
        <div
          className={`folder-item ${isSelected ? 'selected' : ''}`}
          style={{ paddingLeft: `${node.level * 20 + 10}px` }}
        >
          <button
            className="expand-button"
            style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
            onClick={(e) => { e.stopPropagation(); toggleExpansion(node.folderId); }}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
          <span className="folder-icon">📁</span>
          <span
            className="folder-name"
            onClick={() => onFolderChange?.(node.folderId, node.name)}
          >
            {node.name}
          </span>
          <button
            className="btn btn-sm btn-outline-danger folder-delete-btn"
            onClick={(e) => { e.stopPropagation(); handleDeleteFolder(node); }}
            title="Delete folder"
          >
            🗑️
          </button>
        </div>

        {isExpanded && hasChildren && (
          <div className="folder-children">
            {node.children.map(child => renderFolderNode(child))}
          </div>
        )}
      </div>
    );
  };

  if (!user) {
    return (
      <div className={`folder-manager ${className}`}>
        <div className="alert alert-info">
          <p>Folder management is available for registered users only.</p>
          <a href="/register" className="btn btn-primary btn-sm">Create Account</a>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner message="Loading folders..." />;

  return (
    <div className={`folder-manager ${className}`}>
      <div className="folder-manager-header">
        <h5>Folders</h5>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          + New Folder
        </button>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}

      {showCreateModal && (
        <div className="folder-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="folder-modal" onClick={(e) => e.stopPropagation()}>
            <div className="folder-modal-header">
              <h6>New Folder{currentFolderId ? ' (inside current folder)' : ''}</h6>
            </div>
            <div className="folder-modal-body">
              <input
                ref={inputRef}
                type="text"
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="form-control"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateFolder();
                  else if (e.key === 'Escape') setShowCreateModal(false);
                }}
              />
              {error && <div className="text-danger mt-1" style={{ fontSize: '0.85rem' }}>{error}</div>}
            </div>
            <div className="folder-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateFolder}
                disabled={creating || !newFolderName.trim()}
              >
                {creating ? 'Creating…' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="folder-tree">
        <div
          className={`folder-item root-folder ${!currentFolderId ? 'selected' : ''}`}
          onClick={() => onFolderChange?.(undefined, undefined)}
        >
          <div className="folder-content">
            <span className="folder-icon">🏠</span>
            <span className="folder-name">All Documents</span>
          </div>
        </div>

        {folderTree.map(node => renderFolderNode(node))}
      </div>

      {folders.length === 0 && (
        <div className="empty-state">
          <p className="text-muted">No folders yet.</p>
          <button className="btn btn-sm btn-outline-primary" onClick={() => setShowCreateModal(true)}>
            Create your first folder
          </button>
        </div>
      )}
    </div>
  );
};

export default FolderManager;
